import React from "react";
import { Box, Typography, Stack, Chip } from "@mui/material";
import { Replay, CalendarMonth, FilterAlt, CalendarViewWeek, Numbers, Schedule } from "@mui/icons-material";
import { ScheduledRestriction, WEEK_DAYS as ALL_WEEK_DAYS, MONTHS_UI as ALL_MONTHS_UI } from "./TimerConfigurationUtils";

interface TimerSummaryProps {
    activeTab: "recurring" | "single";
    useFrequency: boolean;
    baseInterval: number;
    baseUnit: string;
    startTime: string;
    useMonthFilter: boolean;
    selectedMonths: number[];
    MONTHS_UI: { id: number; name: string }[];
    useDayFilter: boolean;
    selectedDays: number[];
    WEEK_DAYS: { id: string; name: string; value: number }[];
    useDayOfMonthFilter: boolean;
    selectedDaysOfMonth: number[];
    primaryColor: string;
    secondaryColor: string;
    borderColor: string;
    UNIT_LABELS: Record<string, string>;
    getTranslation: (key: string) => string;
    previewDates: Date[];
    locale: string;
    scheduledRestrictions: ScheduledRestriction[];
}

const TimerSummary: React.FC<TimerSummaryProps> = ({
    activeTab,
    useFrequency,
    baseInterval,
    baseUnit,
    startTime,
    useMonthFilter,
    selectedMonths,
    MONTHS_UI,
    useDayFilter,
    selectedDays,
    WEEK_DAYS,
    useDayOfMonthFilter,
    selectedDaysOfMonth,
    primaryColor,
    secondaryColor,
    borderColor,
    UNIT_LABELS,
    getTranslation,
    previewDates,
    locale,
    scheduledRestrictions,
}) => {
    const hasChips = scheduledRestrictions.length > 0;

    /** Short label for a chip, used inside the summary panel. */
    const chipSummaryLabel = (r: ScheduledRestriction): string => {
        const dayParts = r.days.map((v) => {
            const wd = ALL_WEEK_DAYS.find((d) => d.value === v);
            if (!wd) return String(v);
            const tr = getTranslation(
                `pm.propertyeditor.specificproperties.repetition_details.label_weeks.in_the_days.dayoftheweek.${wd.name}`
            );
            return tr ? tr.slice(0, 3) : wd.full.slice(0, 3);
        });
        const monthParts = r.months.map((v) => {
            const mu = ALL_MONTHS_UI.find((m) => m.id === v);
            if (!mu) return String(v);
            const tr = getTranslation(
                `pm.propertyeditor.specificproperties.repetition_details.cycle.label.monthly.${mu.name}`
            );
            return tr ? tr.slice(0, 3) : mu.label;
        });
        const domParts = r.daysOfMonth.slice().sort((a, b) => a - b).map(String);

        const parts: string[] = [];
        if (dayParts.length) parts.push(dayParts.join(", "));
        if (monthParts.length) parts.push(monthParts.join(", "));
        if (domParts.length) {
            const dayLabel = getTranslation("generic.day.caption") || "Giorno";
            parts.push(`${dayLabel} ${domParts.join(",")}`);
        }

        let timeLabel = r.time;
        if (r.useFrequency && r.baseUnit) {
            let unitLabel = "";
            if (r.baseUnit === "minutes") unitLabel = getTranslation("generic.minutes.caption") || "minuti";
            else if (r.baseUnit === "hours") unitLabel = getTranslation("generic.hours.caption") || "ore";
            else if (r.baseUnit === "days") unitLabel = getTranslation("generic.days.caption") || "giorni";
            else if (r.baseUnit === "weeks") unitLabel = getTranslation("pm.propertyeditor.specificproperties.repetition_details.duration.label.weeks") || "settimane";
            else if (r.baseUnit === "months") unitLabel = getTranslation("generic.months.caption") || "mesi";

            const periodStr = `${getTranslation("pm.timer_config.label.repeat_every") || "Ogni"} ${r.baseInterval} ${unitLabel}`;
            timeLabel += ` (${periodStr})`;
        }

        parts.push(timeLabel);
        return parts.join(" · ");
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Typography
                variant="caption"
                fontWeight={700}
                color="black"
                sx={{
                    letterSpacing: 1,
                    mb: 1,
                    display: "block",
                }}
            >
                {getTranslation("pm.timer_config.summary.title") || "Riepilogo"}
            </Typography>
            <Box
                sx={{
                    backgroundColor: "#fff",
                    p: 1.5,
                    borderRadius: "10px",
                    border: `1px solid ${borderColor}`,
                }}
            >
                <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mb: 0.5 }}>
                    {activeTab === "recurring" ? (
                        <Replay sx={{ fontSize: 16, color: primaryColor, mt: 0.3 }} />
                    ) : (
                        <CalendarMonth sx={{ fontSize: 16, color: primaryColor, mt: 0.3 }} />
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.95rem" }}>
                        {activeTab === "recurring"
                            ? hasChips
                                ? (getTranslation("pm.timer_config.summary.scheduled_restrictions") || "Orari pianificati")
                                : useFrequency
                                    ? (getTranslation("pm.timer_config.summary.run_every") || "Ogni ")
                                    : (getTranslation("pm.timer_config.summary.run_at") || "Esecuzione alle ")
                            : (getTranslation("pm.timer_config.summary.once_on") || "Una volta il ")}
                        {activeTab === "recurring" && !hasChips && (
                            <Box component="span" fontWeight={700} color={secondaryColor}>
                                {useFrequency
                                    ? `${baseInterval} ${UNIT_LABELS[baseUnit]}`
                                    : startTime}
                            </Box>
                        )}
                        {activeTab === "single" && (
                            <Box component="span" fontWeight={700} color={secondaryColor}>
                                {previewDates[0]
                                    ? `${previewDates[0].toLocaleDateString(locale)} ${getTranslation("pm.timer_config.summary.at_hours") || "alle ore"} ${previewDates[0].toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}`
                                    : ""}
                            </Box>
                        )}
                    </Typography>
                </Stack>

                {/* ── Chip-based restrictions summary ── */}
                {hasChips && activeTab === "recurring" && (
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                        {scheduledRestrictions.map((r) => (
                            <Stack key={r.id} direction="row" alignItems="center" spacing={0.5}>
                                <Schedule sx={{ fontSize: 14, color: primaryColor }} />
                                <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                                    <Box component="span" fontWeight={600} color={secondaryColor}>
                                        {chipSummaryLabel(r)}
                                    </Box>
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                )}

                {/* ── Legacy restriction summary (no chips) ── */}
                {!hasChips && (
                    <>
                        {useMonthFilter && activeTab === "recurring" && selectedMonths.length > 0 && (
                            <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mb: 0.5 }}>
                                <FilterAlt sx={{ fontSize: 14, color: "#f97316", mt: 0.3 }} />
                                <Typography variant="body2" color="text.secondary">
                                    {getTranslation("pm.timer_config.summary.only_at") || "Solo a "}
                                    <Box component="span" fontWeight={700} color={secondaryColor}>
                                        {selectedMonths
                                            .map((m) =>
                                                getTranslation(
                                                    `pm.propertyeditor.specificproperties.repetition_details.cycle.label.monthly.${MONTHS_UI.find((mu) => mu.id === m)?.name}`
                                                )
                                            )
                                            .join(", ")}
                                    </Box>
                                </Typography>
                            </Stack>
                        )}

                        {useDayFilter && activeTab === "recurring" && selectedDays.length > 0 && (
                            <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                                <CalendarViewWeek sx={{ fontSize: 14, color: "#8b5cf6", mt: 0.3 }} />
                                <Typography variant="body2" color="text.secondary">
                                    {getTranslation("pm.timer_config.summary.only_on") || "Solo il "}
                                    <Box
                                        component="span"
                                        fontWeight={700}
                                        color={secondaryColor}
                                        sx={{ textTransform: "uppercase" }}
                                    >
                                        {selectedDays
                                            .map((d) => {
                                                const dayObj = WEEK_DAYS.find((wd) => wd.value === d);
                                                return getTranslation(
                                                    `pm.propertyeditor.specificproperties.repetition_details.label_weeks.in_the_days.dayoftheweek.${dayObj?.name}`
                                                );
                                            })
                                            .join(", ")}
                                    </Box>
                                </Typography>
                            </Stack>
                        )}

                        {useDayOfMonthFilter && activeTab === "recurring" && selectedDaysOfMonth.length > 0 && (
                            <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mt: 0.5 }}>
                                <Numbers sx={{ fontSize: 14, color: "#10b981", mt: 0.3 }} />
                                <Typography variant="body2" color="text.secondary">
                                    {getTranslation("pm.timer_config.summary.only_on") || "Solo il "}
                                    <Box component="span" fontWeight={700} color={secondaryColor}>
                                        {selectedDaysOfMonth.sort((a, b) => a - b).join(", ")}
                                    </Box>
                                </Typography>
                            </Stack>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default TimerSummary;
